import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme, alpha } from '@mui/material/styles';
import { MONO_FONT } from '@/shared/typography';
import { FORM_CHROME_DENSITY, tabularToolbarBand } from '@/shared/form/formChrome';
import { isLockedCommandId, type PinSlot } from '@/shared/form/commandPin';

/** Shared More-row grid so overflow and chrome switches sit on one right column. */
export const MORE_PIN_GRID = {
  display: 'grid',
  gridTemplateColumns: '1fr 52px',
  alignItems: 'center',
  columnGap: 0,
} as const

export function PinToggle({
  label,
  pinned,
  onChange,
}: {
  label: string
  pinned: boolean
  onChange: (slot: PinSlot) => void
}) {
  return (
    <Switch
      size="small"
      checked={pinned}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(_, checked) => onChange(checked ? 'bar' : 'overflow')}
      slotProps={{ input: { 'aria-label': `Pin ${label}` } }}
      sx={{ justifySelf: 'end' }}
    />
  )
}

export interface CommandItem {
  id?: string;
  label: string;
  icon?: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  busy?: boolean;
  color?: 'primary' | 'error';
  kbd?: string;
  /** Shown on hover; span-wrapped when disabled so the tip still fires. */
  tooltip?: string;
  /** Optional hook for callers that already query by data-testid. */
  testId?: string;
}

export interface CommandBarToolbar {
  /** When true, omit the chrome bottom border (parent already draws a divider). */
  embedded?: boolean;
  /** Extra data-* so list/tabular callers keep their existing test hooks. */
  mark?: 'list' | 'tabular';
}

export interface CommandBarProps {
  primary?: CommandItem[];
  secondary?: CommandItem[];
  overflow?: CommandItem[];
  onPin?: (id: string, slot: PinSlot) => void;
  onResetPins?: () => void;
  /** Kicker left of primary — list record count, etc. */
  leading?: React.ReactNode;
  /** Search (and any chrome that stays on the bar) — sits between primary and secondary. */
  utilities?: React.ReactNode;
  /** View / Group by / Columns / density — rendered inside More, above overflow commands. */
  moreExtras?: React.ReactNode;
  /** Rightmost slot — always after More (e.g. list settings). */
  trailing?: React.ReactNode;
  /**
   * Density-B chrome used by the App list and tabular toolbars.
   * Item-form CommandBar omits this and stays an inline action row.
   */
  toolbar?: boolean | CommandBarToolbar;
}

function toolbarOpts(toolbar: CommandBarProps['toolbar']): CommandBarToolbar | null {
  if (!toolbar) return null;
  if (toolbar === true) return {};
  return toolbar;
}

export const CommandBar: React.FC<CommandBarProps> = ({
  primary = [],
  secondary = [],
  overflow = [],
  onPin,
  onResetPins,
  leading,
  utilities,
  moreExtras,
  trailing,
  toolbar,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const moreWrapRef = useRef<HTMLDivElement>(null);
  const morePanelRef = useRef<HTMLDivElement>(null);
  const moreOpen = Boolean(anchor);
  const theme = useTheme();

  useEffect(() => {
    if (!moreOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (moreWrapRef.current?.contains(t)) return;
      if (morePanelRef.current?.contains(t)) return;
      if (t.closest('.MuiMenu-root, .MuiAutocomplete-popper, .MuiPopover-root, .MuiModal-root, .MuiPopper-root')) return;
      setAnchor(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAnchor(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);
  const chrome = toolbarOpts(toolbar);
  const hasItems = primary.length > 0 || secondary.length > 0 || overflow.length > 0;
  if (!chrome && !hasItems) return null;
  if (chrome && !hasItems && leading == null && utilities == null && moreExtras == null && trailing == null) return null;

  const renderButtonLabel = (item: CommandItem, contained: boolean) => {
    // On a contained (filled) button the chip sits on the button's fill colour,
    // so it must derive from the button's contrast text — text.disabled gray is
    // illegible on primary.main. Outlined/text buttons sit on the surface, so
    // the muted disabled tone reads correctly there.
    const base = contained ? theme.palette.primary.contrastText : theme.palette.text.disabled;
    return (
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {item.label}
        {item.kbd && (
          <Box
            component="span"
            aria-hidden
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '0.625rem',
              color: alpha(base, 0.75),
              border: `1px solid ${alpha(base, 0.35)}`,
              padding: '0.25rem 0.5rem',
              borderRadius: '0.25rem',
              ml: 0.75,
              whiteSpace: 'nowrap',
            }}
          >
            {item.kbd}
          </Box>
        )}
      </Box>
    );
  };

  const withTooltip = (item: CommandItem, node: React.ReactElement) => {
    if (!item.tooltip) return node
    const disabled = Boolean(item.disabled || item.busy)
    // Disabled buttons ignore pointer events — wrap so Tooltip (and native title) still work.
    const target = disabled ? <span title={item.tooltip}>{node}</span> : node
    return (
      <Tooltip title={item.tooltip}>
        {target}
      </Tooltip>
    )
  }

  const renderItems = (items: CommandItem[], keyPrefix: string) =>
    items.map((item, i) => {
      const iconOnly = item.icon != null
      const tip = item.tooltip ?? (iconOnly ? item.label : undefined)
      const control = iconOnly ? (
        <IconButton
          size="small"
          color={item.color === 'error' ? 'error' : 'default'}
          aria-label={item.label}
          data-testid={item.testId}
          onClick={(e) => item.onClick(e)}
          disabled={item.disabled || item.busy}
          sx={{ minHeight: FORM_CHROME_DENSITY.buttonHeight, minWidth: FORM_CHROME_DENSITY.buttonHeight }}
        >
          {item.busy ? <CircularProgress size={16} color="inherit" /> : item.icon}
        </IconButton>
      ) : (
        <Button
          variant="contained"
          size="small"
          disableElevation
          color={item.color ?? 'primary'}
          data-testid={item.testId}
          startIcon={item.busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={(e) => item.onClick(e)}
          disabled={item.disabled || item.busy}
          title={!item.disabled && !item.busy ? item.tooltip : undefined}
        >
          {renderButtonLabel(item, true)}
        </Button>
      )
      return (
        <React.Fragment key={`${keyPrefix}${i}`}>
          {tip
            ? withTooltip({ ...item, tooltip: tip }, control)
            : control}
        </React.Fragment>
      )
    });

  const pinnableBar = onPin
    ? secondary.filter((item) => !isLockedCommandId(item.id ?? item.label))
    : [];
  const moreCommands: { item: CommandItem; pinned: boolean }[] = [
    ...overflow.map((item) => ({ item, pinned: false })),
    ...pinnableBar.map((item) => ({ item, pinned: true })),
  ];

  const more = (moreCommands.length > 0 || moreExtras != null) && (
    <Box ref={moreWrapRef} sx={{ position: 'relative' }}>
      <IconButton
        size="small"
        aria-label="More actions"
        aria-expanded={moreOpen}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Popper
        open={moreOpen}
        anchorEl={anchor}
        placement="bottom-end"
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <Paper
          ref={morePanelRef}
          elevation={8}
          sx={{ py: 0.5, minWidth: 240, maxHeight: '70vh', overflow: 'auto' }}
        >
          {moreCommands.map(({ item, pinned }, i) => (
            <MenuItem
              key={`o${i}`}
              disabled={item.disabled || item.busy}
              title={item.tooltip}
              data-testid={item.testId}
              onClick={(e) => { setAnchor(null); item.onClick(e); }}
              sx={{
                ...MORE_PIN_GRID,
                px: 1.5,
                ...(item.color === 'error' ? { color: 'error.main' } : {}),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {item.busy ? <CircularProgress size={16} color="inherit" /> : item.icon}
                {item.label}
              </Box>
              {onPin ? (
                <PinToggle
                  label={item.label}
                  pinned={pinned}
                  onChange={(slot) => onPin(item.id ?? item.label, slot)}
                />
              ) : <span />}
            </MenuItem>
          ))}
          {moreExtras != null && moreCommands.length > 0 && <Divider />}
          {moreExtras != null && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {moreExtras}
            </Box>
          )}
          {onResetPins && (
            <>
              {(moreCommands.length > 0 || moreExtras != null) && <Divider />}
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onResetPins();
                }}
              >
                Reset pins
              </MenuItem>
            </>
          )}
        </Paper>
      </Popper>
    </Box>
  );

  const rowSx = chrome
    ? {
        display: 'flex' as const,
        alignItems: 'center',
        gap: `${FORM_CHROME_DENSITY.gap}px`,
        px: 1.5,
        py: 0.5,
        minHeight: FORM_CHROME_DENSITY.toolbarHeight,
        flexWrap: 'wrap' as const,
        ...(chrome.embedded ? {} : { borderBottom: 1, borderColor: 'divider' }),
        ...(chrome.mark === 'tabular' ? { bgcolor: tabularToolbarBand(theme) } : {}),
      }
    : { display: 'flex' as const, alignItems: 'center', gap: 1, flexWrap: 'wrap' as const };

  const markAttrs =
    chrome?.mark === 'list'
      ? { 'data-list-toolbar': true as const }
      : chrome?.mark === 'tabular'
        ? { 'data-tabular-toolbar': true as const }
        : {};

  const main = (
    <>
      {leading}
      {renderItems(primary, 'p')}
      {chrome ? <Box sx={{ flex: 1 }} /> : null}
      {utilities}
      {renderItems(secondary, 's')}
      {more}
    </>
  );

  return (
    <Box
      {...(chrome ? { 'data-command-toolbar': true as const, ...markAttrs } : {})}
      sx={trailing != null ? { ...rowSx, flexWrap: 'nowrap' } : rowSx}
    >
      {trailing != null ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: rowSx.gap,
            flex: 1,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          {main}
        </Box>
      ) : (
        main
      )}
      {trailing != null ? <Box sx={{ flexShrink: 0 }}>{trailing}</Box> : null}
    </Box>
  );
};
